import axios, {type Axios} from "axios";
import type {ApiRoutes, CheckFilterValue, LocationValue, Dictionary, FilterValue, Geo} from "./types.ts";
import {computed, ref, type Ref} from "vue";

const debounceDelay = 1000;

const debounce = (callback: Function, delay: number) => {
    let timeOut: number | undefined;
    return function (...args: any[]) {
        clearTimeout(timeOut);
        timeOut = setTimeout(() => {
            callback(...args);
        }, delay);
    }
}

export default class JobApiProvider {
    protected _semaphore = ref(0);

    protected _axios: Axios = axios;

    protected _urlSearchParams: URLSearchParams;

    protected _routes: ApiRoutes;
    protected _filters: Ref<Dictionary<Array<FilterValue>>> = ref({});
    protected _checkFilters: Ref<Array<CheckFilterValue>> = ref([]);

    protected _search = ref('');

    protected _location = ref<LocationValue>({});

    protected _geoLookup = ref('');
    protected _geoSuggestions: Ref<Array<Geo>> = ref([]);

    protected _jobList: Ref<Array<any>> = ref([]);

    constructor(
        routes: ApiRoutes,
        filters: Array<string>,
        checkFilters: Array<CheckFilterValue>,
        protected _config: Dictionary<any>,
        protected _content: Dictionary<any>,
    ) {
        this._axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

        this._urlSearchParams = new URLSearchParams(window.location.search);
        this._routes = routes;

        this.initFilters(filters);
        this.initCheckFilters(checkFilters);

        this._search.value = this._urlSearchParams.get('search');

        this._location = ref<LocationValue>({
            geo: this._urlSearchParams.get('geo'),
            geoId: this._urlSearchParams.get('geo-id'),
            radius: this._urlSearchParams.get('radius')
        });
    }

    initFilters(filters: Array<string>) {
        const filter = filters.shift();

        // end of recursion
        if (!filter) {
            this.performSearch();
            return;
        }

        const selected = this._urlSearchParams.getAll(filter + '[]');

        const url = this._routes.filter + '/' + filter

        const params: Dictionary<any> = {};
        params[filter] = selected;

        this._axios.post(url, null, {
            params: params
        })
            .then(response => {
                this._filters.value[filter] = [];
                for (const values of Object.values(response.data) as Array<FilterValue>) {
                    this._filters.value[filter].push(values);
                }
                this.initFilters(filters);
            });
    }


    initCheckFilters(filters: Array<CheckFilterValue>) {
        this._checkFilters.value = filters.map(filter => {
            filter.checked = this._urlSearchParams.get(filter.queryParameter) === filter.value;
            return filter;
        });
    }

    busy() {
        return computed(() => {
            return this._semaphore.value > 0;
        });
    }

    search(search: string = '') {
        this._search.value = search;
        this.performSearch();
    }

    getLocation(): Ref<LocationValue> {
        return this._location;
    }

    getGeoLookup() {
        return this._geoLookup;
    }

    setGeoLookup(lookup?: string) {
        if (this._routes && lookup && lookup.length > 0) {
            this._geoLookup.value = lookup;
            this._axios.post(
                this._routes.geoLookup,
                null,
                {
                    params: {
                        search: lookup
                    }
                }
            )
                .then(response => {
                    this._geoSuggestions.value = response.data as Array<Geo>
                });
        }
    }

    getSearch(): Ref<string> {
        return this._search;
    }

    setLocationRadius(radius: string) {
        this._location.value.radius = radius;
        this.performSearch();
    }

    setLocation(geo: string, geoId: number) {
        this._location.value.geo = geo;
        this._location.value.geoId = geoId.toString();
        this.performSearch();
    }

    geoReset() {
        this._location.value.geo = '';
        this._location.value.geoId = '';
        this._location.value.radius = '';

        this._geoSuggestions.value = [];
        this.performSearch();
    }

    jobList() {
        return this._jobList;
    }

    geoSuggestions() {
        return this._geoSuggestions;
    }

    filterNames() {
        return Object.keys(this._filters.value);
    }

    filter(name: string) {
        return this._filters.value[name];
    }

    setFilter(name: string, valueName: string, value: null | boolean) {
        const filter = this._filters.value[name].find(item => {
            return item.value === valueName;
        });
        if (filter) {
            // toggle
            value = value === null ? !filter.isSelected : value;
            filter.isSelected = value;
        }
        this.performSearch();
    }

    toggleFilter(name: string, valueName: string) {
        this.setFilter(name, valueName, null);
    }

    getCheckFilters() {
        return this._checkFilters;
    }

    setCheckFilter(filter: CheckFilterValue, value: boolean) {
        // nur 1 checkFilter gleichzeitig erlaubt

        this._checkFilters.value.forEach(f => {
            f.checked = (filter === f) && value;
        });

        this.performSearch();
    }

    resetAll() {
        this._search.value = '';

        this.geoReset();

        this._geoLookup.value = '';

        for (const values of Object.values(this._filters.value)) {
            for (const value of values) {
                value.isSelected = false;
            }
        }

        this._checkFilters.value.forEach(filter => filter.checked = false);

        this.performSearch();
    }

    getConfig(name: string): any {
        return this._config[name];
    }

    getContent(name: string): any {
        return this._content[name];
    }

    performSearch = debounce(() => {
        this._semaphore.value++;
        // set search
        let queryParameter: any = {
            search: this._search.value
        };

        // set geo
        const {geo, geoId, radius} = this._location.value;
        if (geo !== '' && geoId !== '') {
            queryParameter['geo'] = geo;
            queryParameter['geo-id'] = geoId;
            queryParameter['radius'] = radius;
        }

        // set filters
        for (const filterName in this._filters.value) {
            queryParameter[filterName] = this._filters.value[filterName]
                .filter(item => {
                    return item.isSelected;
                })
                .map(item => {
                    return item.value
                });
        }

        // set check filters
        for (const [filterName, settings] of Object.entries(this._checkFilters.value)) {
            if (settings.checked) {
                queryParameter[settings.queryParameter] = settings.value;
            }
        }

        if (this._routes) {
            this._axios.post(
                this._routes.search,
                null,
                {
                    params: queryParameter
                }
            )
                .then(result => {
                    this._jobList.value = result.data;
                })
                .catch(error => {
                    this._jobList.value = [];
                })
                .finally(() => {
                    this._semaphore.value--;
                });
        }
    }, debounceDelay);
}
